@if (filteredOptions$ | async; as filteredOptions) {
  @if (selections$ | async; as selections) {
    @if (label(); as label) {
      <label class="text-xs font-medium text-foreground-primary-60">
        {{ label }}
        @if (isRequired()) {
          <span class="text-status-error">*</span>
        }
      </label>
    }
    @if (inline()) {
      <div
        class="flex flex-col w-[240px] rounded-radius-primary text-sm whitespace-nowrap bg-background-fixed px-2"
        [ngClass]="dropdownClasses()"
      >
        <ng-container *ngTemplateOutlet="dropdownInner"></ng-container>
      </div>
    } @else {
      <div
        cdkOverlayOrigin
        #trigger="cdkOverlayOrigin"
        class="flex flex-col relative"
        [class.cursor-pointer]="!isDisabled && !isLoading()"
        [class.cursor-default]="isDisabled || isLoading()"
        (click)="onTriggerClicked()"
      >
        @if (customTriggerDirective(); as customTriggerDirective) {
          <ng-container *ngTemplateOutlet="customTriggerDirective.templateRef; context: { openedDropdown: isDropdownOpen }"></ng-container>
        } @else {
          <div
            class="flex items-center justify-between gap-2 rounded-radius-container font-semibold text-sm text-action-primary whitespace-nowrap bg-background-fixed px-[13px] border relative"
            [ngClass]="[
              isDisabled
                ? 'placeholder:[&&&]:text-custom-disabled-input-color [&&&]:text-custom-disabled-input-color [&&&&]:bg-custom-disabled-input-background'
                : '',
              selectClasses(),
              size() === 'large' ? 'h-[45px]' : 'h-[36px]',
              error() ? 'border-status-error' : 'border-[transparent]',
            ]"
          >
            @if (isLoading()) {
              <app-loading-skeleton backgroundTextClass="text-background-fixed" class="w-[50px] h-5" viewBox="0 0 50 20">
                <svg:rect x="0" y="0" rx="5" ry="5" width="50px" height="20px" />
              </app-loading-skeleton>
            } @else {
              <span class="w-full overflow-hidden text-ellipsis">
                @if (selections) {
                  @if (selections.length > 1) {
                    @if (selections.length >= ((optionsCount$ | async) || 0)) {
                      @if (allSelectedLabelTranslationKey(); as allSelectedLabelKey) {
                        {{ allSelectedLabelKey | translate: { count: selections.length | number } }}
                      } @else {
                        {{
                          allSelectedLabel()
                            ? allSelectedLabel()
                            : ('shared.multi-select.all-selection' | translate: { count: selections.length | number })
                        }}
                      }
                    } @else {
                      {{
                        selectedLabel()
                          ? (selections.length | number) + ' ' + selectedLabel()
                          : ('shared.multi-select.selection' | translate: { count: (selections.length | number) })
                      }}
                    }
                  } @else {
                    @if (selections.length && selections[0].value !== null) {
                      {{ selections[0].label }}
                    } @else {
                      <ng-container *ngTemplateOutlet="placeholderView"></ng-container>
                    }
                  }
                } @else {
                  <ng-container *ngTemplateOutlet="placeholderView"></ng-container>
                }

                <ng-template #placeholderView>
                  <span class="text-text-400">{{
                    placeholder() ||
                      (type() === 'multi'
                        ? selectedLabel()
                          ? '0 ' + selectedLabel()
                          : ('shared.multi-select.selection' | translate: { count: 0 })
                        : '')
                  }}</span>
                </ng-template>
              </span>
            }

            <div class="flex items-center pointer-events-none">
              <svg-icon key="chevron-down-16" size="16"></svg-icon>
            </div>
          </div>
        }

        @if (!errorInFlow()) {
          <div class="absolute top-[calc(100%_+_4px)] left-0 w-full text-xs font-medium text-status-error">
            {{ error() ?? '' }}
          </div>
        }
      </div>

      @if (errorInFlow() && error(); as error) {
        <div class="w-full text-xs font-medium text-status-error">{{ error }}</div>
      }

      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="trigger"
        [cdkConnectedOverlayOpen]="isDropdownOpen"
        [cdkConnectedOverlayPositions]="customPositions() ?? positions"
        [cdkConnectedOverlayHasBackdrop]="false"
        [cdkConnectedOverlayPush]="true"
        (overlayOutsideClick)="onOutsideClick($event)"
        (overlayKeydown)="onOverlayKeydown($event)"
      >
        <div
          class="flex flex-col w-[240px] rounded-radius-container text-sm whitespace-nowrap bg-background-fixed drop-shadow-dropdown"
          [ngClass]="dropdownClasses()"
        >
          <ng-container *ngTemplateOutlet="dropdownInner"></ng-container>
        </div>
      </ng-template>
    }

    <ng-template #dropdownInner>
      @if (withSearch() || (withSelectAll() && type() === 'multi')) {
        <div
          class="flex flex-col gap-y-2 border-foreground-primary-12"
          [ngClass]="{
            'border-b': !inline(),
            'pt-3': !withSearch(),
            '[&&]:pt-2': withSearch() && inline(),
            '[&&]:pb-0': withSearch() && inline(),
            'pt-1': withSearch(),
            'pb-1': withSearch() && !(withSelectAll() && type() === 'multi'),
            'pb-3': withSelectAll() && type() === 'multi',
          }"
        >
          @if (withSearch()) {
            <app-text-input
              [placeholder]="'shared.multi-select.search-placeholder' | translate"
              leftIcon="search-20"
              [inputClasses]="`text-base !pl-[36px] ${inline() ? 'border-action-primary' : ''}`"
              [(ngModel)]="search"
              [ngModelOptions]="{ standalone: true }"
              [clearable]="true"
            ></app-text-input>
          }

          @if (withSelectAll() && type() === 'multi') {
            <div class="flex flex-col" [ngClass]="[inline() ? 'px-3' : '']">
              <label
                [for]="selectUuid"
                class="flex items-center gap-2 cursor-pointer font-semibold text-action-primary"
                [ngClass]="[inline() ? 'py-2 border-foreground-primary-12 border-b' : '', !inline() ? 'px-3' : '']"
              >
                <app-checkbox
                  [indeterminate]="isSelectAllIndeterminate"
                  [(ngModel)]="isSelectAll"
                  [ngModelOptions]="{ standalone: true }"
                  [id]="selectUuid"
                  (ngModelChange)="onMultiSelectAllChange($event, filteredOptions)"
                ></app-checkbox>
                <span>
                  {{ 'shared.multi-select.select-all' | translate }}
                </span>
              </label>
            </div>
          }
        </div>
      }

      <div
        class="grow flex flex-col max-h-[300px] overflow-y-auto"
        [ngClass]="[!isConfirm ? 'rounded-b-radius-container' : '', optionsContainerClasses()]"
      >
        @if (filteredOptions?.length) {
          @for (option of filteredOptions; track option.value; let last = $last) {
            <div
              class="shrink-0 flex flex-col"
              [ngClass]="{
                'px-3': inline(),
                'border-b border-foreground-primary-12': !last && !inline(),
                'font-semibold text-action-primary': option.selected,
                'cursor-pointer hover:bg-background-container-tertiary': !option.disabled,
                'cursor-default opacity-50': option.disabled,
              }"
              (click)="onOptionClick(option.value, option.disabled)"
            >
              @if (customOptionDirective(); as customOptionDirective) {
                <ng-container *ngTemplateOutlet="customOptionDirective.templateRef; context: { $implicit: option }"></ng-container>
              } @else {
                <div
                  class="shrink-0 flex flex-col"
                  [ngClass]="[inline() ? 'h-[37px]' : '', !inline() ? 'h-[45px]' : '', !option.disabled ? 'cursor-pointer' : '']"
                >
                  @if (type() === 'multi') {
                    <div class="grow flex items-center gap-2" [class.px-3]="!inline()">
                      <app-checkbox
                        [ngModel]="option.selected"
                        [ngModelOptions]="{ standalone: true }"
                        class="pointer-events-none"
                      ></app-checkbox>
                      <span>{{ option.label }}</span>
                    </div>
                  } @else {
                    <div class="grow flex items-center" [class.px-3]="!inline()">{{ option.label }}</div>
                  }
                </div>
              }
            </div>
          }
        } @else {
          <div class="flex shrink-0 items-center h-[45px] pl-3">{{ 'shared.multi-select.no-result' | translate }}</div>
        }
      </div>
      @if (isConfirm) {
        <div class="flex justify-between gap-2 border-t border-foreground-primary-12 px-3 py-2">
          <app-button class="flex-1" type="secondary" (clickEvent)="onCancelClick()">
            <div class="text-center">
              {{ 'shared.multi-select.cancel' | translate }}
            </div>
          </app-button>

          <app-button class="flex-1" type="primary" (clickEvent)="onConfirmClick()">
            <div class="text-center">
              {{ 'shared.multi-select.save' | translate }}
            </div>
          </app-button>
        </div>
      }
    </ng-template>
  }
}
